/**
 * Format a minor-unit amount for display.
 *
 * Why not just Intl: `Intl.NumberFormat(undefined, {currency:'MNT'})` renders
 * "MNT 15,000", because Node/browsers only reach for the ₮ symbol under a
 * Mongolian locale — and forcing `mn-MN` globally would render AUD as
 * "$ 15,000", losing the A$ distinction that matters to this product's
 * Australian side. So tögrög is handled explicitly, in the suffix position
 * Mongolian actually uses ("15,000₮", as the agency's own price sheet writes
 * it), and everything else goes through Intl where it is already correct.
 *
 * Amounts are MINOR units everywhere in this app (quote_items.unit_price,
 * invoices.total, offerings.price_amount), so the /100 lives here rather
 * than at each call site.
 *
 * No decimals: no price list quotes tögrög or dollars to the cent.
 */
const SUFFIX_SYMBOLS: Record<string, string> = {
  MNT: '₮'
}

export function formatMinor(minor: number | null | undefined, currency = 'MNT'): string {
  if (minor === null || minor === undefined) return ''

  const major = minor / 100
  const cur = currency.toUpperCase()
  const suffix = SUFFIX_SYMBOLS[cur]

  if (suffix) {
    return `${major.toLocaleString('en-US', { maximumFractionDigits: 0 })}${suffix}`
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur,
      maximumFractionDigits: 0
    }).format(major)
  } catch {
    // Unknown/invalid currency code — never throw on a public page.
    return `${major.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${cur}`
  }
}
